import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Alert } from '@mui/material';
import Profile from '../sections/@dashboard/account/Profile';
import { useAuthContext } from '../auth/useAuthContext';
import { setSession } from '../auth/utils';
import axios from '../utils/Axios';
import saveErrorMessage from '../utils/saveErrorMessage';

export default function ProfilePage() {
  const { user, setGlobalUser } = useAuthContext();
  const [status, setStatus] = useState(null);
  const dismissTimer = useRef(null);

  useEffect(() => () => clearTimeout(dismissTimer.current), []);

  // A rejected save is shown with the server's own message and re-raised, so the form
  // can record the save as unsuccessful and keep its unsaved-changes warning. The form
  // catches it; see account/Profile.js.
  const updateUser = useCallback(async (userData) => {
    clearTimeout(dismissTimer.current);

    // Only the request is inside the try, so a failure after a successful save is not
    // reported as a failed save.
    let response;
    try {
      response = await axios.put(`/user`, userData);
    } catch (error) {
      console.error('Profile save failed', error);
      setStatus({ severity: 'error', message: saveErrorMessage(error, 'We could not save your profile. Please try again.') });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      throw error;
    }

    // A new password ends the old token on the server. The answer then carries the new
    // one, and storing it as login does keeps this browser logged in.
    const { token } = response.data;
    if (typeof token === 'string' && token) {
      setSession(token, localStorage.getItem('user'));
    }

    setGlobalUser(response.data.user);
    setStatus({ severity: 'success', message: 'Settings Updated.' });
    dismissTimer.current = setTimeout(() => setStatus(null), 6000);
  }, [setGlobalUser]);

  return (
    <>
      <Helmet>
        <title>Profile | {localStorage.getItem("companyName") ?? "FlowBookings"}</title>
      </Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          Profile
        </Typography>
        {status != null && (
          <Alert
            severity={status.severity}
            onClose={status.severity === 'error' ? () => setStatus(null) : undefined}
            sx={{ mb: 2, whiteSpace: 'pre-line' }}
          >
            {status.message}
          </Alert>
        )}
        <Profile user={user} updateUser={updateUser} />
      </Container>
    </>
  );
}
